import type { DatoxExtraction } from "./api"
import type { DataCategory } from "./automations"
import { reconcileBindings, type CategoryDefinition, type CategoryField, type FactsheetWorkspace, type WorkspaceSave } from "./workspaces"
import { reportTables } from "./report-data"

export type WorkflowCategory = DataCategory & { definition?: CategoryDefinition }

export function workflowCategories(definitions: CategoryDefinition[], applied: DataCategory[]): WorkflowCategory[] {
  return [...definitions.map((category) => ({
    ...category, id: `definition:${category.id}`, key: category.id, definition: category,
    inferred: false, fields: category.fields, source_file_ids: [], extraction_id: "", workflow_run_id: null, status: "defined",
  })), ...applied]
}

export function categoryValidation(category: CategoryDefinition, definitions: CategoryDefinition[]) {
  if (!category.name.trim()) return "name" as const
  if (definitions.some((item) => item.id !== category.id && item.name.trim().toLowerCase() === category.name.trim().toLowerCase())) return "duplicateCategory" as const
  if (definitions.length >= 100 && !definitions.some((item) => item.id === category.id)) return "limit" as const
  if (!category.fields.length) return "fields" as const
  if (category.fields.some((field) => !field.name.trim())) return "fieldName" as const
  if (new Set(category.fields.map((field) => field.name.trim().toLowerCase())).size !== category.fields.length) return "duplicateField" as const
  if (category.fields.some((field) => field.type === "enum" && (!field.choices.length || field.choices.length > 200))) return "choices" as const
}

export function categorySaveBody(workspace: FactsheetWorkspace, category: CategoryDefinition | CategoryDefinition[]): WorkspaceSave {
  const saved = (Array.isArray(category) ? category : [category]).map((category) => ({ ...category, name: category.name.trim(), description: category.description.trim(), fields: category.fields.map((field) => ({
    ...field, name: field.name.trim(), description: field.description.trim(), unit: field.unit.trim(),
    choices: field.type === "enum" ? [...new Set(field.choices.map((choice) => choice.trim()).filter(Boolean))] : [],
  })) }))
  const definition = { ...workspace.definition, categories: [
    ...workspace.definition.categories.map((item) => saved.find((category) => category.id === item.id) ?? item),
    ...saved.filter((category) => !workspace.definition.categories.some((item) => item.id === category.id)),
  ] }
  return { name: workspace.name, revision: workspace.revision, definition_revision: workspace.definition_revision, period_id: workspace.period_id,
    definition, config: { ...workspace.config, bindings: reconcileBindings(definition, workspace.config.bindings) } }
}

export function categorySuggestionRun(workspace: FactsheetWorkspace, fileIDs: string[]) {
  const sameFiles = JSON.stringify([...new Set(fileIDs)].sort()) === JSON.stringify([...workspace.config.file_ids].sort())
  return workspace.runs.find((run) => run.workspace_action === "suggest" && run.workspace_revision === workspace.revision &&
    run.project_id === workspace.project_id && run.period_id === workspace.period_id &&
    (["pending", "running", "waiting_for_files", "interrupted"].includes(run.status) || sameFiles && run.status === "completed"))
}

export function suggestedCategories(rows: DatoxExtraction[], workspace: FactsheetWorkspace, runID: string) {
  const matching = rows.filter((row) => row.project_id === workspace.project_id && row.period_id === workspace.period_id && !row.case_id &&
    row.workflow_run_id === runID && row.result_json.workspace_id === workspace.id && row.status === "created")
    .sort((left, right) => right.updated_at.localeCompare(left.updated_at))
  const unique = new Map<string, CategoryDefinition>()
  for (const category of matching.flatMap(extractionCategories)) {
    const key = category.name.trim().toLowerCase()
    if (!unique.has(key)) unique.set(key, category)
  }
  return [...unique.values()]
}

export function extractionCategories(extraction: DatoxExtraction | undefined): CategoryDefinition[] {
  if (!extraction) return []
  const categories = extraction.result_json.categories
  const tables = reportTables(extraction.result_json.tables)
  const rows = Array.isArray(categories) && categories.length ? categories : tables.map((table) => ({ name: table.name, fields: table.columns }))
  return rows.flatMap((value, index) => {
    if (!value || typeof value !== "object") return []
    const category = value as Record<string, unknown>
    const name = String(category.name || category.label || category.key || tables[index]?.name || "").trim()
    if (!name) return []
    const fields = Array.isArray(category.fields) ? category.fields : tables[index]?.columns ?? []
    return [{ id: `${extraction.id.slice(0, 40)}-c${index}`, name,
      description: `${String(category.description || "")}\nSource extraction: ${extraction.id}; category: ${String(category.id || category.key || name)}`.trim(),
      fields: fields.flatMap((value, fieldIndex): CategoryField[] => {
        const field = typeof value === "string" ? { name: value } : value && typeof value === "object" ? value as Record<string, unknown> : {}
        if (!field.name) return []
        const type = String(field.type || "text")
        return [{ id: `${extraction.id.slice(0, 35)}-c${index}-f${fieldIndex}`, name: String(field.name),
          type: (["text", "number", "date", "boolean", "enum"].includes(type) ? type : "text") as CategoryField["type"],
          description: String(field.description || ""), unit: String(field.unit || ""), required: field.required === true,
          choices: Array.isArray(field.choices) ? field.choices.filter((choice): choice is string => typeof choice === "string") : [] }]
      }) }]
  })
}
